import { useEffect } from 'react';
import { Leaf, Sun, Droplets, Sprout, Heart, Users, TreePine, Apple } from 'lucide-react';

const WhenauGardening = () => {
  useEffect(() => {
    document.title = 'Whānau Gardening | He Whakaputanga nga Hapū o te Tairawhiti';
    return () => {
      document.title = 'He Whakaputanga nga Hapū o te Tairawhiti | Customary Rights Platform';
    };
  }, []);

  const traditionalPlants = [
    { name: "Kumara (Sweet Potato)", description: "Traditional staple crop, perfect for autumn planting", season: "Spring-Summer" },
    { name: "Taro", description: "Nutritious root vegetable, grows in moist conditions", season: "Year-round" },
    { name: "Yam", description: "Hardy climbing vine with edible tubers", season: "Spring-Summer" },
    { name: "Gourd (Hue)", description: "Multi-purpose plant for food and containers", season: "Summer" },
    { name: "Karaka", description: "Native tree with edible berries (requires processing)", season: "Autumn harvest" },
    { name: "Pikopiko (Fern)", description: "Native fern shoots, harvested sustainably", season: "Spring" }
  ];

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header Section */}
      <section className="bg-gradient-to-r from-green-600 to-green-700 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Whenua & Gardening
          </h1>
          <p className="text-xl md:text-2xl max-w-4xl mx-auto opacity-90">
            Reconnecting with the land through traditional and sustainable gardening practices
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="section-header">Nurturing the Bond with Whenua</h2>
            <div className="card bg-green-50">
              <p className="text-lg text-gray-700 leading-relaxed">
                Gardening on whenua is more than food production - it's a sacred relationship with Papatūānuku (Earth Mother). 
                Traditional Māori gardening practices combined with modern sustainable techniques create abundance 
                while honoring the mauri (life force) of the land.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            <div className="card text-center">
              <Heart className="h-10 w-10 text-green-600 mx-auto mb-3" />
              <h3 className="text-lg font-semibold mb-2 text-gray-800">Spiritual Connection</h3>
              <p className="text-gray-600 text-sm">Honoring Papatūānuku through respectful cultivation</p>
            </div>

            <div className="card text-center">
              <Users className="h-10 w-10 text-green-600 mx-auto mb-3" />
              <h3 className="text-lg font-semibold mb-2 text-gray-800">Whānau Wellbeing</h3>
              <p className="text-gray-600 text-sm">Providing healthy kai for the whole whānau</p>
            </div>

            <div className="card text-center">
              <TreePine className="h-10 w-10 text-green-600 mx-auto mb-3" />
              <h3 className="text-lg font-semibold mb-2 text-gray-800">Environmental Care</h3>
              <p className="text-gray-600 text-sm">Practicing kaitiakitanga (guardianship)</p>
            </div>

            <div className="card text-center">
              <Sprout className="h-10 w-10 text-green-600 mx-auto mb-3" />
              <h3 className="text-lg font-semibold mb-2 text-gray-800">Cultural Revival</h3>
              <p className="text-gray-600 text-sm">Preserving traditional knowledge and practices</p>
            </div>
          </div>
        </div>
      </section>

      {/* Traditional Plants */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="section-header text-center mb-12">Traditional Māori Plants</h2>
          
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {traditionalPlants.map((plant, index) => (
                <div key={index} className="card hover:shadow-lg transition-shadow">
                  <div className="flex items-start mb-3">
                    <Leaf className="h-6 w-6 text-green-600 mr-3 mt-1" />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">{plant.name}</h3>
                      <span className="text-sm text-green-600 font-medium">{plant.season}</span>
                    </div>
                  </div>
                  <p className="text-gray-600">{plant.description}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 card bg-green-50">
              <h3 className="text-xl font-semibold mb-4 text-green-700">Cultural Protocol</h3>
              <p className="text-gray-700">
                When harvesting traditional plants, especially native species, always:
                • Ask permission from kaumātua and landowners
                • Take only what you need
                • Leave the strongest plants to regenerate
                • Offer karakia (prayers) before and after harvesting
                • Share knowledge with the next generation
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sustainable Practices */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="section-header text-center mb-12">Sustainable Gardening Practices</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="card">
              <Droplets className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="text-xl font-semibold mb-3 text-gray-800">Water Stewardship</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Collect and store rainwater</li>
                <li>• Use mulching to retain moisture</li>
                <li>• Install drip irrigation systems</li>
                <li>• Plant water-wise native species</li>
                <li>• Create swales for natural drainage</li>
                <li>• Practice greywater recycling</li>
              </ul>
            </div>

            <div className="card">
              <Sun className="h-8 w-8 text-yellow-600 mb-4" />
              <h3 className="text-xl font-semibold mb-3 text-gray-800">Soil Health</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Composting organic matter</li>
                <li>• No-dig gardening methods</li>
                <li>• Crop rotation principles</li>
                <li>• Natural pest management</li>
                <li>• Companion planting</li>
                <li>• Cover cropping in winter</li>
              </ul>
            </div>

            <div className="card">
              <TreePine className="h-8 w-8 text-green-600 mb-4" />
              <h3 className="text-xl font-semibold mb-3 text-gray-800">Biodiversity</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Plant native trees and shrubs</li>
                <li>• Create habitats for beneficial insects</li>
                <li>• Maintain wild areas on property</li>
                <li>• Use heirloom and native seeds</li>
                <li>• Encourage bird and bee populations</li>
                <li>• Integrate food forest principles</li>
              </ul>
            </div>

            <div className="card">
              <Apple className="h-8 w-8 text-ocean-teal mb-4" />
              <h3 className="text-xl font-semibold mb-3 text-gray-800">Food Security</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Grow staple crops for whānau</li>
                <li>• Preserve and store harvests</li>
                <li>• Share surplus with community</li>
                <li>• Save seeds for next season</li>
                <li>• Establish fruit and nut trees</li>
                <li>• Create medicinal plant gardens</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Seasonal Calendar */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="section-header text-center mb-12">Māori Seasonal Calendar</h2>
          
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="cultural-border">
                <h3 className="text-xl font-semibold mb-3 text-green-600">Raumati (Summer) - Nov-Jan</h3>
                <p className="text-gray-600 mb-3">Time of abundance and harvest</p>
                <ul className="text-gray-600 space-y-1">
                  <li>• Harvest kumara and summer crops</li>
                  <li>• Preserve food for winter storage</li>
                  <li>• Collect seeds for next season</li>
                  <li>• Plant second crops where possible</li>
                </ul>
              </div>

              <div className="cultural-border">
                <h3 className="text-xl font-semibold mb-3 text-green-600">Ngahuru (Autumn) - Feb-Apr</h3>
                <p className="text-gray-600 mb-3">Time of gathering and preparation</p>
                <ul className="text-gray-600 space-y-1">
                  <li>• Final harvest of main crops</li>
                  <li>• Collect native berries and nuts</li>
                  <li>• Prepare gardens for winter</li>
                  <li>• Plant winter vegetables</li>
                </ul>
              </div>

              <div className="cultural-border">
                <h3 className="text-xl font-semibold mb-3 text-green-600">Takurua (Winter) - May-Jul</h3>
                <p className="text-gray-600 mb-3">Time of rest and planning</p>
                <ul className="text-gray-600 space-y-1">
                  <li>• Rest the soil and compost</li>
                  <li>• Plan next season's gardens</li>
                  <li>• Maintain tools and structures</li>
                  <li>• Share preserved foods</li>
                </ul>
              </div>

              <div className="cultural-border">
                <h3 className="text-xl font-semibold mb-3 text-green-600">Koanga (Spring) - Aug-Oct</h3>
                <p className="text-gray-600 mb-3">Time of new growth and planting</p>
                <ul className="text-gray-600 space-y-1">
                  <li>• Prepare garden beds</li>
                  <li>• Plant kumara and summer crops</li>
                  <li>• Harvest winter vegetables</li>
                  <li>• Collect spring plants like pikopiko</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Getting Started */}
      <section className="py-16 bg-green-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Start Your Journey with Whenua
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto opacity-90">
            Whether you have a small backyard or access to ancestral lands, 
            you can begin nurturing your relationship with Papatūānuku today.
          </p>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="text-center">
              <h3 className="text-lg font-semibold mb-2">Begin Small</h3>
              <p className="text-green-100">Start with container gardens or small plots</p>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold mb-2">Learn Together</h3>
              <p className="text-green-100">Share knowledge with whānau and community</p>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold mb-2">Honor the Land</h3>
              <p className="text-green-100">Practice kaitiakitanga in all your gardening</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhenauGardening;
